'use client';

import { useHelpSection } from '@/lib/hooks/useSiteData';

const HelpSection = () => {
  const { data: helpData, loading, error } = useHelpSection();

  // Show loading state
  if (loading) {
    return (
      <section className="help-sec">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="help-img">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 align-self-center">
              <div className="help-content">
                <div className="placeholder-glow">
                  <h2 className="placeholder col-8"></h2>
                  <p className="placeholder col-12"></p>
                  <p className="placeholder col-10"></p>
                  <p className="placeholder col-6"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="help-sec">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="help-img">
                <img src="/images/boxes/help_img.png" alt="Help and support illustration" />
              </div>
            </div>
            <div className="col-lg-6 align-self-center">
              <div className="help-content">
                <h2>We're Here to Help You Navigate Your Journey</h2>
                <p>
                  Since 2001 VirginiaNavigator and our family of websites has been the trusted nonprofit
                  resource for older adults, people with disabilities, veterans, and their caregivers and
                  families. Search our resource directory of 26,000 programs and services to connect with
                  local, statewide, and national resources to help you navigate your journey.
                </p>
                <a href="#" className="btn btn-secondary">
                  <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                  Read More
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Helper to get spec value from help data
  const getSpecValue = (specs: any[], key: string, defaultValue: string = '') => {
    if (!specs || !Array.isArray(specs)) return defaultValue;
    const spec = specs.find((s: any) => s.key === key);
    return spec?.value || defaultValue;
  };

  // Use API data if available, otherwise fallback to static content
  const helpSectionData = helpData?.data;
  const title = getSpecValue(helpSectionData?.specifications || [], 'title', 'We\'re Here to Help You Navigate Your Journey');
  const imageUrl = getSpecValue(helpSectionData?.specifications || [], 'image', '/images/boxes/help_img.png');
  const buttonText = getSpecValue(helpSectionData?.specifications || [], 'button-text', 'Read More');
  const buttonLink = getSpecValue(helpSectionData?.specifications || [], 'button-link', '/page/help-and-support');
  
  // Decode content if available
  let content = 'Since 2001 VirginiaNavigator and our family of websites has been the trusted nonprofit resource for older adults, people with disabilities, veterans, and their caregivers and families. Search our resource directory of 26,000 programs and services to connect with local, statewide, and national resources to help you navigate your journey.';
  
  if (helpSectionData?.content) {
    try {
      const decoded = atob(helpSectionData.content); 
    
      // Convert Latin-1 bytes → UTF-8 string
      const utf8Decoder = new TextDecoder('utf-8');
      const bytes = Uint8Array.from(decoded, c => c.charCodeAt(0));
      content = utf8Decoder.decode(bytes);
    
      // Remove HTML tags for preview
      content = content.replace(/<[^>]*>/g, '');
    } catch (e) {
      // fallback
    }
  }

  return (
    <section className="help-sec">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-6">
            <div className="help-img">
              <img src={imageUrl} alt="Help and support illustration" />
            </div>
          </div>
          <div className="col-lg-6 align-self-center">
            <div className="help-content">
              <h2>{title}</h2>
              <p>{content}</p>
              
              {buttonLink && buttonLink !== '/' && (
                <a href={buttonLink} className="btn btn-secondary">
                  <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                  {buttonText}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HelpSection;
